import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Crown, 
  Check, 
  X, 
  Sparkles, 
  ArrowLeft,
  CreditCard,
  Calendar,
  Shield,
  Settings,
  LogIn
} from 'lucide-react';
import { usePremium, formatPrice, PREMIUM_FEATURES } from '../hooks/usePremium';
import { useAuthStore } from '../stores/authStore';
import { createCheckoutSession, openCustomerPortal } from '../services/stripe';
import toast from 'react-hot-toast';

export default function PremiumPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { player, token } = useAuthStore();
  const { 
    isPremium, 
    daysRemaining, 
    expiresAt, 
    isLoading,
    prices,
    refresh
  } = usePremium();
  
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Vérifier si on revient de Stripe
  const paymentStatus = searchParams.get('payment');
  if (paymentStatus === 'success') {
    // Rafraîchir le statut premium après un paiement réussi
    setTimeout(() => refresh(), 1000);
  }
  
  // Vérifier si l'utilisateur peut souscrire (doit avoir un compte)
  const canSubscribe = token !== null;
  
  // Gérer l'achat via Stripe Checkout
  const handlePurchase = async () => {
    if (!player || player.type === 'guest') {
      toast.error('Connectez-vous pour souscrire à Premium');
      navigate('/');
      return;
    }
    
    if (!canSubscribe) {
      toast.error('Créez un compte pour souscrire à Premium');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      await createCheckoutSession(selectedPlan);
      // La redirection vers Stripe Checkout est gérée dans le service
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors du paiement');
      setIsProcessing(false);
    }
  };
  
  // Ouvrir le portail client Stripe pour gérer l'abonnement
  const handleManageSubscription = async () => {
    if (!player) return;
    
    try {
      await openCustomerPortal();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de l\'ouverture du portail');
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-3">
            <Crown className="w-8 h-8 text-yellow-400" />
            BlindTest Premium
          </h1>
          <p className="text-dark-400">Débloquez toutes les fonctionnalités</p>
        </div>
      </div>
      
      {/* Statut actuel si déjà premium */}
      {isPremium && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/50 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-yellow-500/30 flex items-center justify-center">
              <Crown className="w-8 h-8 text-yellow-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-yellow-400">Vous êtes Premium ! 🎉</h2>
              {expiresAt ? (
                <p className="text-dark-300">
                  Votre abonnement expire le {expiresAt.toLocaleDateString('fr-FR')}
                  {daysRemaining > 0 && ` (${daysRemaining} jours restants)`}
                </p>
              ) : (
                <p className="text-dark-300">Abonnement à vie ✨</p>
              )}
            </div>
            <button
              onClick={handleManageSubscription}
              className="btn-secondary flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Gérer
            </button>
            <Sparkles className="w-8 h-8 text-yellow-400 animate-pulse" />
          </div>
        </motion.div>
      )}
      
      {/* Message de succès après paiement */}
      {paymentStatus === 'success' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/30 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="font-bold text-green-400">Paiement réussi ! 🎉</h3>
              <p className="text-dark-300">Votre abonnement Premium est maintenant actif.</p>
            </div>
          </div>
        </motion.div>
      )}
      
      {paymentStatus === 'cancel' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card bg-gradient-to-r from-orange-500/20 to-amber-500/20 border-orange-500/50 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-500/30 flex items-center justify-center">
              <X className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h3 className="font-bold text-orange-400">Paiement annulé</h3>
              <p className="text-dark-300">Vous pouvez réessayer quand vous le souhaitez.</p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Plans de tarification */}
      {!isPremium && (
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Plan Mensuel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className={`card cursor-pointer transition-all ${
              selectedPlan === 'monthly' 
                ? 'border-primary-500 ring-2 ring-primary-500/50' 
                : 'hover:border-dark-500'
            }`}
            onClick={() => setSelectedPlan('monthly')}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">Mensuel</h3>
                <p className="text-dark-400">Facturé chaque mois</p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'monthly' 
                  ? 'border-primary-500 bg-primary-500' 
                  : 'border-dark-500'
              }`}>
                {selectedPlan === 'monthly' && <Check className="w-4 h-4" />}
              </div>
            </div>
            
            <div className="mb-4">
              <span className="text-4xl font-bold">{formatPrice(prices.monthly)}</span>
              <span className="text-dark-400">/mois</span>
            </div>
            
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-dark-300">
                <Calendar className="w-4 h-4" />
                Sans engagement
              </li>
              <li className="flex items-center gap-2 text-dark-300">
                <CreditCard className="w-4 h-4" />
                Annulez à tout moment
              </li>
            </ul>
          </motion.div>
          
          {/* Plan Annuel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className={`card cursor-pointer transition-all relative overflow-hidden ${
              selectedPlan === 'yearly' 
                ? 'border-primary-500 ring-2 ring-primary-500/50' 
                : 'hover:border-dark-500'
            }`}
            onClick={() => setSelectedPlan('yearly')}
          >
            {/* Badge économie */}
            <div className="absolute top-4 right-4 px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
              -33% 🎉
            </div>
            
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">Annuel</h3>
                <p className="text-dark-400">Facturé une fois par an</p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'yearly' 
                  ? 'border-primary-500 bg-primary-500' 
                  : 'border-dark-500'
              }`}>
                {selectedPlan === 'yearly' && <Check className="w-4 h-4" />}
              </div>
            </div>
            
            <div className="mb-4">
              <span className="text-4xl font-bold">{formatPrice(prices.yearly)}</span>
              <span className="text-dark-400">/an</span>
              <p className="text-sm text-green-400">
                soit {formatPrice(Math.round(prices.yearly / 12))}/mois
              </p>
            </div>
            
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-green-400">
                <Sparkles className="w-4 h-4" />
                2 mois gratuits !
              </li>
              <li className="flex items-center gap-2 text-dark-300">
                <Shield className="w-4 h-4" />
                Meilleur rapport qualité/prix
              </li>
            </ul>
          </motion.div>
        </div>
      )}
      
      {/* Message pour créer un compte */}
      {!isPremium && !canSubscribe && player && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-blue-500/50 mb-6"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-500/30 flex items-center justify-center">
              <LogIn className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-blue-400">Créez un compte pour souscrire</h3>
              <p className="text-dark-300 text-sm">
                Pour souscrire à Premium, vous devez avoir un compte avec email. 
                Créez-en un ou connectez-vous avec Google.
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="btn-primary bg-blue-500 hover:bg-blue-400"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Créer un compte
            </button>
          </div>
        </motion.div>
      )}
      
      {/* Bouton d'achat */}
      {!isPremium && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mb-12"
        >
          <button
            onClick={handlePurchase}
            disabled={isProcessing || !canSubscribe || (player?.type === 'guest')}
            className="btn-primary px-12 py-4 text-lg font-bold bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                Traitement...
              </span>
            ) : !canSubscribe ? (
              'Créez un compte pour souscrire'
            ) : player?.type === 'guest' ? (
              'Connectez-vous pour devenir Premium'
            ) : (
              <>
                <Crown className="w-5 h-5 inline mr-2" />
                Devenir Premium - {formatPrice(selectedPlan === 'monthly' ? prices.monthly : prices.yearly)}
                {selectedPlan === 'yearly' && '/an'}
              </>
            )}
          </button>
          
          <p className="text-dark-500 text-sm mt-4">
            Paiement sécurisé par Stripe • Annulez à tout moment
          </p>
        </motion.div>
      )}
      
      {/* Liste des avantages */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center">
          {isPremium ? 'Vos avantages Premium' : 'Tout ce que vous obtenez'}
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          {PREMIUM_FEATURES.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + index * 0.05 }}
              className="card-hover flex items-start gap-4"
            >
              <div className="text-3xl">{feature.icon}</div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">{feature.title}</h3>
                <p className="text-sm text-dark-400 mb-2">{feature.description}</p>
                
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1 text-dark-500">
                    {typeof feature.free === 'boolean' ? (
                      feature.free ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <X className="w-3 h-3 text-red-500" />
                      )
                    ) : (
                      <span className="text-dark-400">{feature.free}</span>
                    )}
                    <span>Gratuit</span>
                  </span>
                  
                  <span className="flex items-center gap-1 text-yellow-400">
                    {typeof feature.premium === 'boolean' ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <span>{feature.premium}</span>
                    )}
                    <span>Premium</span>
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
      
      {/* FAQ */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-12"
      >
        <h2 className="text-2xl font-bold mb-6 text-center">Questions fréquentes</h2>
        
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-semibold mb-2">Comment fonctionne l'abonnement ?</h3>
            <p className="text-dark-400 text-sm">
              Votre abonnement Premium est activé immédiatement après le paiement. 
              Il se renouvelle automatiquement à la fin de chaque période, sauf si vous l'annulez.
            </p>
          </div>
          
          <div className="card">
            <h3 className="font-semibold mb-2">Puis-je annuler à tout moment ?</h3>
            <p className="text-dark-400 text-sm">
              Oui ! Vous pouvez annuler votre abonnement à tout moment depuis votre profil. 
              Vous conserverez les avantages Premium jusqu'à la fin de la période payée.
            </p>
          </div>
          
          <div className="card">
            <h3 className="font-semibold mb-2">Quels moyens de paiement acceptez-vous ?</h3>
            <p className="text-dark-400 text-sm">
              Nous acceptons les cartes bancaires (Visa, Mastercard, American Express) 
              via notre partenaire de paiement sécurisé Stripe.
            </p>
          </div>
          
          <div className="card">
            <h3 className="font-semibold mb-2">J'ai un problème avec mon paiement</h3>
            <p className="text-dark-400 text-sm">
              Contactez-nous à support@theblindtest.ch et nous résoudrons votre problème rapidement !
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
